<template>
  <div class="set-password-container">
    <img src="../../../assets/images/login_bg.jpg" alt="" class="image">

    <div class="login-form">
      <div class="title-container">
        <div>
          <img src="../../../assets/images/logo.png" alt="" style="width: 100%;">
        </div>
        <h3 class="title">新用户激活 / 密码设置</h3>
      </div>

      <!-- 链接验证中 -->
      <div v-if="validating" class="status-panel">
        <p class="status-desc" style="padding: 20px 0;">正在验证安全链接，请稍候...</p>
      </div>

      <!-- Token 无效或已失效状态 -->
      <div v-else-if="tokenInvalid" class="status-panel">
        <div class="status-icon">
          <CircleClose style="width: 48px; height: 48px; color: #f56c6c;" />
        </div>
        <h4 class="status-title">链接已失效或过期</h4>
        <p class="status-desc">{{ errorMsg || '此安全链接已失效或超过30分钟有效期。请返回登录页重新申请。' }}</p>
        <el-button type="primary" style="width: 100%; margin-top: 22px;" @click="goToLogin">返回登录页</el-button>
      </div>

      <!-- 设密成功状态 -->
      <div v-else-if="isSuccess" class="status-panel">
        <div class="status-icon">
          <CircleCheck style="width: 48px; height: 48px; color: #67c23a;" />
        </div>
        <h4 class="status-title">密码设置成功！</h4>
        <p class="status-desc">您的新密码已设置，账号已激活。请使用工号和新密码登录系统。</p>
        <el-button type="primary" style="width: 100%; margin-top: 22px;" @click="goToLogin">立即去登录</el-button>
      </div>

      <!-- 表单填写状态 -->
      <div v-else-if="tokenValid">
        <div class="user-greeting">
          您好，<span class="user-highlight">{{ userDetail.name || userDetail.username }}</span>（工号：{{ userDetail.username }}），请设置系统登录密码：
        </div>

        <el-form
          ref="pwdFormRef"
          :model="pwdForm"
          :rules="pwdRules"
          autocomplete="off"
          label-position="left"
        >
          <el-form-item prop="newPassword">
            <span class="svg-container">
              <i class="iconfont icon-mima" />
            </span>
            <el-input
              ref="newPasswordRef"
              v-model="pwdForm.newPassword"
              type="password"
              show-password
              placeholder="请输入新密码"
              clearable
              autocomplete="new-password"
            />
          </el-form-item>

          <el-form-item prop="confirmNewPassword">
            <span class="svg-container">
              <i class="iconfont icon-mima" />
            </span>
            <el-input
              ref="confirmNewPasswordRef"
              v-model="pwdForm.confirmNewPassword"
              type="password"
              show-password
              placeholder="请再次确认新密码"
              clearable
              autocomplete="new-password"
              @keyup.enter="handleSubmit"
            />
          </el-form-item>

          <div class="pwd-tip">
            <div class="tip-title">密码安全要求：</div>
            <div class="tip-item">• 长度为 10 ~ 100 位，不能包含空格</div>
            <div class="tip-item">• 必须同时包含英文字母、数字和特殊字符</div>
          </div>

          <el-button
            type="primary"
            style="width: 100%;"
            :loading="submitting"
            @click.prevent="handleSubmit"
          >
            完成设置并激活
          </el-button>
          <p @click="goToLogin" style="text-align: center; color: #fff; margin-top: 15px;" class="cursor">返回登录</p>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { CircleCheck, CircleClose } from '@element-plus/icons-vue'
import { checkActivationToken, activateAndSetPassword } from '@/apis/user'

document.title = '设置新密码'

export default defineComponent({
  name: 'SetPassword',
  components: {
    CircleCheck,
    CircleClose
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const pwdFormRef = ref<any>(null)
    const newPasswordRef = ref<any>(null)
    const confirmNewPasswordRef = ref<any>(null)

    const validatePass = (rule: any, value: string, callback: any) => {
      if (!value) {
        return callback(new Error('请输入新密码'))
      }
      if (value.length < 10 || value.length > 100) {
        return callback(new Error('密码长度必须为10到100位'))
      }
      const hasLetter = /[a-zA-Z]/.test(value)
      const hasDigit = /[0-9]/.test(value)
      const hasSpecial = /[^a-zA-Z0-9\s]/.test(value)
      const hasWhitespace = /\s/.test(value)

      if (hasWhitespace) {
        return callback(new Error('密码不能包含空格'))
      }
      if (!hasLetter || !hasDigit || !hasSpecial) {
        return callback(new Error('密码必须包含数字、字母及特殊字符'))
      }
      callback()
    }

    const validatePassConfirm = (rule: any, value: string, callback: any) => {
      if (!value) {
        return callback(new Error('请再次输入新密码'))
      }
      if (value !== state.pwdForm.newPassword) {
        return callback(new Error('两次输入的密码不一致'))
      }
      callback()
    }

    const state = reactive({
      tokenId: '',
      validating: true,
      tokenValid: false,
      tokenInvalid: false,
      isSuccess: false,
      errorMsg: '',
      submitting: false,
      userDetail: {
        username: '',
        name: ''
      },
      pwdForm: {
        newPassword: '',
        confirmNewPassword: ''
      },
      pwdRules: {
        newPassword: [{ required: true, validator: validatePass, trigger: ['blur', 'change'] }],
        confirmNewPassword: [{ required: true, validator: validatePassConfirm, trigger: ['blur', 'change'] }]
      }
    })

    const checkToken = () => {
      const id = (route.query.id as string) || ''
      state.tokenId = id
      if (!id) {
        state.validating = false
        state.tokenInvalid = true
        state.errorMsg = '缺少有效的激活令牌参数'
        return
      }

      state.validating = true
      checkActivationToken({ id })
        .then((res: any) => {
          state.validating = false
          state.tokenValid = true
          state.userDetail = res || {}
        })
        .catch((err: any) => {
          state.validating = false
          state.tokenInvalid = true
          state.errorMsg = err?.message || '链接已失效或超过30分钟有效期'
        })
    }

    const handleSubmit = () => {
      if (!pwdFormRef.value) return
      pwdFormRef.value.validate((valid: boolean) => {
        if (!valid) return
        state.submitting = true
        activateAndSetPassword({
          id: state.tokenId,
          newPassword: state.pwdForm.newPassword,
          confirmNewPassword: state.pwdForm.confirmNewPassword
        })
          .then(() => {
            state.submitting = false
            state.isSuccess = true
            ElMessage.success('密码设置成功！正在跳转至登录页...')
            setTimeout(() => {
              goToLogin()
            }, 2500)
          })
          .catch((err: any) => {
            state.submitting = false
            ElMessage.error(err?.message || '密码设置失败，请稍后重试')
          })
      })
    }

    const goToLogin = () => {
      router.push('/login')
    }

    onMounted(() => {
      checkToken()
    })

    return {
      pwdFormRef,
      newPasswordRef,
      confirmNewPasswordRef,
      ...toRefs(state),
      handleSubmit,
      goToLogin
    }
  }
})
</script>

<style lang="scss" scoped>
.set-password-container {
  height: 100vh;
  width: 100%;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  position: relative;

  .image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
  }

  .login-form {
    position: relative;
    width: 360px;
    max-width: 90%;
    margin: 0 auto;
    overflow: hidden;
  }

  .svg-container {
    padding: 6px 5px;
    color: #fff;
    vertical-align: middle;
    width: 30px;
    display: inline-block;
    i {
      font-size: 20px;
    }
  }

  .title-container {
    position: relative;

    .title {
      font-size: 26px;
      color: #fff;
      margin: 0px auto 20px auto;
      text-align: center;
      font-weight: bold;
    }
  }

  :deep(.el-input) {
    display: inline-block;
    height: 47px;
    width: 85%;
    margin-bottom: 6px;

    .el-input__wrapper,
    &.is-focus .el-input__wrapper {
      width: 100%;
      border-radius: 0;
      border: none;
      box-shadow: 0 0 0 0 !important;
      background: transparent;
      padding-left: 0;
    }

    input {
      font-size: 16px;
      height: 47px;
      background: transparent;
      border: 0px;
      border-radius: 0px;
      padding: 12px 5px 12px 10px;
      color: #ffffff;
      caret-color: #ffffff;
      -webkit-appearance: none;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
        font-size: 14px;
      }

      &:-webkit-autofill {
        box-shadow: 0 0 0px 1000px #2d3a4b inset !important;
        -webkit-text-fill-color: #ffffff !important;
      }
    }

    .el-input__suffix {
      .el-icon {
        color: rgba(255, 255, 255, 0.7);
        font-size: 16px;
        cursor: pointer;
        &:hover {
          color: #ffffff;
        }
      }
    }
  }

  :deep(.el-form-item) {
    border-bottom: 1px solid #FFFFFF;
    background: rgba(0, 0, 0, 0);
    margin-bottom: 22px;
  }

  :deep(.el-form-item__error) {
    color: #ff7875;
    font-size: 12px;
    padding-top: 4px;
  }

  .user-greeting {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 20px;
    line-height: 1.6;
    text-align: center;

    .user-highlight {
      color: #409EFF;
      font-weight: bold;
    }
  }

  .pwd-tip {
    margin-top: 15px;
    margin-bottom: 22px;
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 4px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
    line-height: 1.6;

    .tip-title {
      color: #fff;
      font-weight: 500;
      margin-bottom: 4px;
    }
    .tip-item {
      padding-left: 2px;
    }
  }

  .status-panel {
    text-align: center;
    padding: 24px 16px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    margin-top: 10px;

    .status-icon {
      display: flex;
      justify-content: center;
      margin-bottom: 16px;
    }
    .status-title {
      font-size: 20px;
      font-weight: bold;
      color: #fff;
      margin: 0 0 10px 0;
    }
    .status-desc {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.8);
      line-height: 1.6;
      margin: 0;
    }
  }

  .cursor {
    cursor: pointer;
    user-select: none;
    transition: opacity 0.2s;
    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
